import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Film, Trash2, RefreshCw, Search, Ban, Eye, Clock,
  User, AlertTriangle, BarChart2, ChevronLeft, ChevronRight
} from 'lucide-react';
import { API, API_BASE_URL } from '../config/api';

export default function AdminStoriesPage() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stories, setStories] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [includeExpired, setIncludeExpired] = useState(false);
  const limit = 20;

  useEffect(() => {
    if (!user || (user.role !== 'owner' && user.role !== 'admin')) {
      navigate('/');
      return;
    }
    fetchStories();
    fetchStats();
  }, [user, page, includeExpired]);

  const fetchStories = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (page * limit).toString(),
        include_expired: includeExpired.toString()
      });

      const res = await fetch(`${API}/admin/stories?${params}`, {
        headers: getAuthHeaders()
      });

      if (res.ok) {
        const data = await res.json();
        setStories(data.stories || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch stories:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/admin/stories/stats`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleDeleteStory = async (storyId) => {
    if (!confirm('Delete this story? This cannot be undone.')) return;

    try {
      const res = await fetch(`${API}/admin/stories/${storyId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success('Story removed');
        fetchStories();
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete story');
      }
    } catch (err) {
      toast.error('Failed to delete story');
    }
  };

  const handleBanUser = async (userId, userName) => {
    const reason = prompt(`Ban ${userName} from posting stories? Enter reason:`, 'Repeated story policy violations');
    if (!reason) return;

    try {
      const res = await fetch(`${API}/admin/stories/ban-user/${userId}`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      if (res.ok) {
        toast.success(`${userName} banned from posting stories`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to ban user');
      }
    } catch (err) {
      toast.error('Failed to ban user');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString();
  };

  const isExpired = (story) => {
    if (!story.expires_at) return false;
    return new Date(story.expires_at) < new Date();
  };

  const getMediaUrl = (story) => {
    if (story.media?.secure_url) {
      return story.media.secure_url.startsWith('http') 
        ? story.media.secure_url 
        : `${API_BASE_URL}${story.media.secure_url}`;
    }
    if (story.media_url) {
      return story.media_url.startsWith('http') 
        ? story.media_url 
        : `${API_BASE_URL}${story.media_url}`;
    }
    return null;
  };

  return (
    <div className="min-h-screen p-6" data-testid="admin-stories-page">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Film className="w-6 h-6 text-secondary" />
              Story Management
            </h1>
            <p className="text-muted-foreground">Moderate and manage user stories</p>
          </div>
          <Button onClick={() => { fetchStories(); fetchStats(); }} variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                  <Film className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Stories</p>
                  <p className="text-2xl font-bold">{stats.active_stories}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BarChart2 className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">This Week</p>
                  <p className="text-2xl font-bold">{stats.stories_this_week}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Views (24h)</p>
                  <p className="text-2xl font-bold">{stats.views_24h}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Top Creators</p>
                  <p className="text-2xl font-bold">{stats.top_creators?.length || 0}</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="p-4">
          <div className="flex items-center gap-4">
            <Button
              variant={!includeExpired ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setIncludeExpired(false)}
            >
              Active Only
            </Button>
            <Button
              variant={includeExpired ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setIncludeExpired(true)}
            >
              Include Expired
            </Button>
            <span className="text-sm text-muted-foreground ml-auto">
              Total: {total} stories
            </span>
          </div>
        </Card>

        {/* Stories List */}
        <div className="space-y-3">
          {loading ? (
            <Card className="p-8 text-center">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading stories...
            </Card>
          ) : stories.length === 0 ? (
            <Card className="p-8 text-center">
              <Film className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-lg font-medium">No stories found</p>
            </Card>
          ) : (
            stories.map((story) => (
              <Card key={story.story_id} className={`p-4 ${isExpired(story) ? 'opacity-60' : ''}`}>
                <div className="flex items-start gap-4">
                  {/* Media Preview */}
                  <div className="w-20 h-28 rounded-lg overflow-hidden bg-muted/50 flex-shrink-0">
                    {getMediaUrl(story) ? (
                      story.media_type === 'video' ? (
                        <video src={getMediaUrl(story)} className="w-full h-full object-cover" />
                      ) : (
                        <img src={getMediaUrl(story)} alt="" className="w-full h-full object-cover" />
                      )
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Film className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Story Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge className={story.media_type === 'video' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}>
                        {story.media_type === 'video' ? 'Video' : 'Image'}
                      </Badge>
                      {isExpired(story) && (
                        <Badge className="bg-red-500/20 text-red-400">Expired</Badge>
                      )}
                      {story.expired && (
                        <Badge className="bg-yellow-500/20 text-yellow-400">Marked Expired</Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mb-1">
                      {story.user_avatar ? (
                        <img src={story.user_avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-muted/60 flex items-center justify-center">
                          <User className="w-3 h-3" />
                        </div>
                      )}
                      <span className="font-medium">@{story.user_handle || 'unknown'}</span>
                      <span className="text-sm text-muted-foreground">{story.user_name}</span>
                      {story.user_role && (
                        <Badge variant="outline" className="text-xs">{story.user_role}</Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(story.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {story.view_count || 0} views
                      </span>
                      {story.reactions?.length > 0 && (
                        <span>{story.reactions.length} reactions</span>
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground mt-1">
                      ID: {story.story_id}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBanUser(story.user_id, story.user_handle || story.user_name)}
                      className="text-orange-400 hover:text-orange-300"
                    >
                      <Ban className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteStory(story.story_id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="flex items-center px-4 text-sm text-muted-foreground">
              Page {page + 1} of {Math.ceil(total / limit)}
            </span>
            <Button
              variant="outline"
              disabled={(page + 1) * limit >= total}
              onClick={() => setPage(p => p + 1)}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Top Creators Section */}
        {stats?.top_creators?.length > 0 && (
          <Card className="p-4">
            <h3 className="font-medium mb-4 flex items-center gap-2">
              <User className="w-4 h-4" />
              Top Story Creators (This Week)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {stats.top_creators.map((creator, idx) => (
                <div key={creator._id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                  <span className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center text-xs font-bold">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">@{creator.handle || 'user'}</p>
                    <p className="text-xs text-muted-foreground">{creator.count} stories</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
